import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

export default function DailySalesSummaryPrintPopup({ isOpen, onPrintComplete, data }) {
  useEffect(() => {
    if (isOpen) {
      const style = document.createElement('style');
      style.id = 'print-style-popup';
      style.innerHTML = `
        @media print {
          body * {
            visibility: hidden;
          }
          .section-to-print-daily-summary, 
          .section-to-print-daily-summary * {
            visibility: visible;
          }
          .section-to-print-daily-summary {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          @page {
            size: 80mm auto;
            margin: 0mm;
          }
        }
      `;
      document.head.appendChild(style);

      const printTimer = setTimeout(() => {
        window.print();

        if (onPrintComplete) {
          setTimeout(() => {
            onPrintComplete();
          }, 500);
        }
      }, 800);

      return () => {
        clearTimeout(printTimer);
        const styleElement = document.getElementById('print-style-popup');
        if (styleElement) {
          styleElement.remove();
        }
      };
    }
  }, [isOpen, onPrintComplete]);

  if (!isOpen || !data) return null;

  const formatNumber = num => {
    if (num === null || num === undefined) return '0.00';
    return Number(num).toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  return createPortal(
    <div className="section-to-print-daily-summary print:block hidden">
      <div className="flex flex-col justify-center items-center text-black p-8">
        <div className="w-full max-w-xs bg-white p-4 flex flex-col">
          <div className="text-center mb-4">
            <div className="text-lg font-semibold">สรุปยอดขายรวมสิ้นวัน ณ วันที่: {data.date}</div>
            <div className="text-sm">
              วันที่พิมพ์: {data.currentDate} {data.printTime} น
            </div>
            <div className="font-medium">
              {data.company.name} - {data.company.branch} ({data.company.branchCode})
            </div>
            {data.company.taxId && (
              <div>
                TAX ID: {data.company.taxId}{' '}
                {data.company.vatCalculationCode
                  ? data.company.vatCalculationCode === 'INCLUDED'
                    ? '(VAT Included)'
                    : data.company.vatCalculationCode === 'SEPARATE'
                    ? '(VAT Excluded)'
                    : ''
                  : data.company.vatIncluded
                  ? '(VAT Included)'
                  : ''}
              </div>
            )}
          </div>

          <div className="border-dashed border-t border-black my-2"></div>

          <div className="flex flex-col gap-1 mb-2">
            <div className="flex justify-between">
              <div>จำนวนบิลขาย</div>
              <div>{data.tickets.sold}</div>
            </div>
            <div className="flex justify-between">
              <div>จำนวนบิลที่ยกเลิก</div>
              <div>{data.tickets.canceled}</div>
            </div>
            <div className="flex justify-between">
              <div>จำนวนบิล Void</div>
              <div>{data.tickets.void}</div>
            </div>
            <div className="flex justify-between">
              <div>จำนวนบิลที่คืนสินค้า</div>
              <div>{data.tickets.refund}</div>
            </div>
            <div className="flex justify-between">
              <div>จำนวนบิล ออกใบกำกับภาษีเต็มรูปแบบ</div>
              <div>{data.tickets.taxInvoice}</div>
            </div>
            <div className="flex justify-between">
              <div>มูลค่า ออกใบกำกับภาษี เต็มรูปแบบ</div>
              <div>{formatNumber(data.tickets.taxInvoiceValue)}</div>
            </div>
          </div>

          <div className="border-dashed border-t border-black my-2"></div>

          {data.sales.initialCash !== undefined && (
            <div className="flex justify-between">
              <div>เงินสดตั้งต้น</div>
              <div>{formatNumber(data.sales.initialCash)}</div>
            </div>
          )}
          <div className="flex justify-between">
            <div>ยอดขาย</div>
            <div>{formatNumber(data.sales.total)}</div>
          </div>
          <div className="flex justify-between">
            <div>-Sale Refund</div>
            <div>{formatNumber(data.sales.saleRefund)}</div>
          </div>
          <div className="flex justify-between">
            <div>-Affer Refund</div>
            <div>{formatNumber(data.sales.afterRefund)}</div>
          </div>
          <div className="flex justify-between">
            <div>-Item Discount</div>
            <div>{formatNumber(data.sales.itemDiscount)}</div>
          </div>
          <div className="flex justify-between">
            <div>-Sub Discount</div>
            <div>{formatNumber(data.sales.subDiscount)}</div>
          </div>
          <div className="flex justify-between">
            <div>-Point Discount</div>
            <div>{formatNumber(data.sales.pointDiscount)}</div>
          </div>
          <div className="flex justify-between">
            <div>-Coupon Discount</div>
            <div>{formatNumber(data.sales.couponDiscount)}</div>
          </div>
          {data.sales.totalSales !== undefined && (
            <div className="flex justify-between">
              <div>ยอดรวมขาย</div>
              <div>{formatNumber(data.sales.totalSales)}</div>
            </div>
          )}
          <div className="flex justify-between">
            <div>ค่าธรรมเนียม</div>
            <div>{formatNumber(data.sales.serviceFee)}</div>
          </div>
          <div className="flex justify-between">
            <div className="whitespace-nowrap font-bold">ยอดสุทธิ</div>
            <div className="text-right font-bold">
              {formatNumber(data.sales.totalFinalAmount || data.sales.netAmount)}
            </div>
          </div>

          <div className="border-dashed border-t border-black my-2"></div>

          {/* ค่าธรรมเนียมบัตรเครดิตและภาษี (ถ้ามี) */}
          {data.sales.creditCardFee !== undefined && (
            <>
              <div className="flex justify-between">
                <div>ค่าธรรมเนียมบัตรเครดิต</div>
                <div>{formatNumber(data.sales.creditCardFee)}</div>
              </div>
              <div className="flex justify-between">
                <div>ภาษีมูลค่าเพิ่ม</div>
                <div>{formatNumber(data.sales.vatOnFee)}</div>
              </div>
              <div className="border-dashed border-t border-black my-2"></div>
            </>
          )}

          <div className="flex justify-between">
            <div>มูลค่าสินค้าไม่มีภาษี</div>
            <div>{formatNumber(data.sales.nonTaxableAmount)}</div>
          </div>
          <div className="flex justify-between">
            <div>มูลค่าสินค้ามีภาษี</div>
            <div>{formatNumber(data.sales.taxableAmount)}</div>
          </div>
          <div className="flex justify-between">
            <div>ภาษีมูลค่าเพิ่ม</div>
            <div>{formatNumber(data.sales.vat)}</div>
          </div>

          {/* Net Amount และ Net Amount Adj. (ถ้ามี) */}
          {data.sales.totalPaymentAmount !== undefined && (
            <div className="flex flex-col justify-center px-16">
              <div className="flex justify-between w-full font-semibold">
                <div>Net Amount</div>
                <div>{formatNumber(data.sales.totalPaymentAmount)}</div>
              </div>
              {data.sales.netAmountAdj !== undefined && (
                <div className="flex justify-between w-full font-semibold">
                  <div>Net Amount Adj.</div>
                  <div>{formatNumber(data.sales.netAmountAdj)}</div>
                </div>
              )}
            </div>
          )}
          {data.sales.totalPaymentAmount === undefined && (
            <div className="flex justify-between font-bold text-lg">
              <div>Net Amount</div>
              <div>{formatNumber(data.sales.netAmount)}</div>
            </div>
          )}

          <div className="border-dashed border-t border-black my-2"></div>

          {/* ช่องทางการชำระเงิน */}
          <div className="font-medium">ช่องทางการชำระเงิน</div>

          {/* ถ้ามีข้อมูล system_balance จะใช้วิธีเดียวกับที่แสดงใน DailySalesSummary */}
          {data.payments.systemBalance && data.payments.systemBalance.length > 0 ? (
            data.payments.systemBalance.map((paymentMethod, index) => (
              <div key={index}>
                <div className="flex justify-between">
                  <div className="whitespace-nowrap">{paymentMethod.payment_method_name}</div>
                  <div className="text-right">
                    {paymentMethod.payment_providers &&
                      paymentMethod.payment_providers.length === 0 &&
                      formatNumber(paymentMethod.amount)}
                  </div>
                </div>

                {paymentMethod.payment_providers &&
                  paymentMethod.payment_providers.map((provider, providerIndex) => (
                    <div className="flex justify-between" key={`provider-${providerIndex}`}>
                      <div className="whitespace-nowrap pl-4">- {provider.payment_provider_name}</div>
                      <div className="text-right">{formatNumber(provider.amount)}</div>
                    </div>
                  ))}
              </div>
            ))
          ) : (
            <>
              {/* ถ้าไม่มีข้อมูล system_balance จะใช้ข้อมูลเดิม */}
              <div className="flex justify-between">
                <div>โอนเงิน</div>
                <div>{formatNumber(data.payments.bankTransfer.total)}</div>
              </div>
              {data.payments.bankTransfer.details &&
                data.payments.bankTransfer.details.map((bank, index) => (
                  <div className="flex justify-between pl-4" key={index}>
                    <div>- {bank.name}</div>
                    <div>{formatNumber(bank.amount)}</div>
                  </div>
                ))}
              <div className="flex justify-between">
                <div>เงินสด</div>
                <div>{formatNumber(data.payments.cash)}</div>
              </div>
              <div className="flex justify-between">
                <div>บัตรเดบิต/เครดิต</div>
                <div>{formatNumber(data.payments.creditCard)}</div>
              </div>
              <div className="flex justify-between">
                <div>เดลิเวอรี่</div>
                <div>{formatNumber(data.payments.delivery.total)}</div>
              </div>
              {data.payments.delivery.details &&
                data.payments.delivery.details.map((delivery, index) => (
                  <div className="flex justify-between pl-4" key={index}>
                    <div>- {delivery.name}</div>
                    <div>{formatNumber(delivery.amount)}</div>
                  </div>
                ))}
            </>
          )}

          <div className="border-dashed border-t border-black my-2"></div>

          {/* กำไร(ขาดทุน)ปัดเศษ (ถ้ามี) */}
          {data.payments.lossRounded !== undefined && (
            <div className="flex justify-between">
              <div>กำไร(ขาดทุน)ปัดเศษ</div>
              <div>{formatNumber(data.payments.lossRounded)}</div>
            </div>
          )}

          <div className="flex justify-between">
            <div>ยอดนับเงินสด</div>
            <div>{formatNumber(data.payments.cashTotal || data.payments.cashRemaining)}</div>
          </div>

          <div className="flex justify-between font-semibold px-16">
            <div>Diff</div>
            <div>{formatNumber(data.payments.summaryDiff || 0)}</div>
          </div>

          <div className="border-dashed border-t border-black my-4"></div>
        </div>
      </div>
    </div>,
    document.body
  );
}
